import React, {
  useState,
  createContext,
  useContext,
  ComponentProps,
  useEffect,
  useRef,
} from "react";
import classNames from "classnames";
import {twMerge} from "tailwind-merge";

export interface TabsComponentsProps extends ComponentProps<"div"> {
  children: React.ReactNode[];
  currentIndex?: number;
  onTabChange?: (index: number) => void;
}

interface ContentProps {
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
}

export interface TabProps extends ComponentProps<"div"> {
  index?: number;
  disabled?: boolean;
}

const CreateTabsContext = createContext<ContentProps>({
  activeIndex: 0,
  setActiveIndex: () => null,
});

const useContextValue = () => useContext(CreateTabsContext);

export const TabList = ({children, className}: ComponentProps<"div">) => {
  const childElem = React.Children.map<React.ReactNode, React.ReactNode>(
    children,
    (child, index) => {
      if (!React.isValidElement(child)) return;
      return React.cloneElement(child as React.ReactElement<TabProps>, {
        index,
      });
    },
  );
  return (
    <div
      className={twMerge("flex gap-4 overflow-x-auto scroll-hidden", className)}
    >
      {childElem}
    </div>
  );
};

export const Tab = ({children, index = 0, disabled, className}: TabProps) => {
  const {activeIndex, setActiveIndex} = useContextValue();
  const tabRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeIndex !== index) return;
    tabRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "center",
    });
  }, [activeIndex]);

  return (
    <div
      ref={tabRef}
      onClick={() => !disabled && setActiveIndex(index)}
      className={twMerge(
        classNames(
          "flex flex-col gap-1 justify-end items-center py-1 transition-all duration-100 cursor-pointer",
          "text-primaryBlue-700 bg-white border-b-2 border-transparent whitespace-nowrap",
          {"hover:border-primary-600": activeIndex !== index && !disabled},
          {"border-primary-600 font-semibold": activeIndex === index},
          {
            "cursor-not-allowed text-slate-600 hover:border-slate-300":
              disabled,
          },
        ),
        className,
      )}
    >
      {children}
    </div>
  );
};

export const TabPanels = ({children, className}: ComponentProps<"div">) => {
  const {activeIndex} = useContextValue();
  return (
    <div className={twMerge("mx-2 mt-4", className)}>
      {children && React.Children.toArray(children)[activeIndex]}
    </div>
  );
};

export const TabContent = ({children}: ComponentProps<"div">) => (
  <div>{children}</div>
);

export const Tabs = ({
  children,
  currentIndex = 0,
  onTabChange,
}: TabsComponentsProps) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);
  useEffect(() => {
    if (onTabChange && currentIndex !== activeIndex) {
      onTabChange(activeIndex);
    }
  }, [activeIndex]);
  return (
    <CreateTabsContext.Provider
      value={{
        activeIndex,
        setActiveIndex,
      }}
    >
      {children}
    </CreateTabsContext.Provider>
  );
};
